import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import type { UserType } from "../types/role.types";

interface UserTypeListResponse {
  data: UserType[];
}

export const useUserTypes = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-types'],
    queryFn: async () => {
      const response = await defaultInstance<UserTypeListResponse>({
        url: '/user_type',
        method: 'GET',
      });
      return (response as unknown as UserTypeListResponse).data || [];
    },
  });

  return {
    userTypes: (data as UserType[] | undefined) || [],
    isLoading,
    error,
  };
};

export const useCreateUserType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<UserType>) => {
      return await defaultInstance({
        url: '/user_type',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-types'] });
      toaster.positive('Role created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create role', { autoHideDuration: 3000 });
    },
  });
};

export const useUpdateUserType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<UserType> }) => {
      return await defaultInstance({
        url: `/user_type/${id}`,
        method: 'PUT',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-types'] });
      toaster.positive('Role updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update role', { autoHideDuration: 3000 });
    },
  });
};

export const useDeleteUserType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await defaultInstance({
        url: `/user_type/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-types'] });
      toaster.positive('Role deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete role', { autoHideDuration: 3000 });
    },
  });
};
